import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';

import {
  ALL_COMPETITIONS_ITEM,
  ALL_TEAMS_ITEM,
  FilterKey,
  Options,
  homeAwayOptions,
} from '@sorare/marketplace/src/lib/performance';

import type { useGetFiltersOptions_playerGameScoreInterface } from './__generated__/useGetFiltersOptions.graphql';

export const useGetFiltersOptions = <
  T extends useGetFiltersOptions_playerGameScoreInterface,
>({
  so5Scores,
}: {
  so5Scores: T[];
}): Options => {
  const teams = so5Scores.reduce(
    (acc, score) => {
      const { anyTeam } = score.anyPlayerGameStats;
      if (!anyTeam) return acc;
      if (!acc.find(t => t.value === anyTeam.slug)) {
        acc.push({
          value: anyTeam.slug,
          label: (
            <LabelM bold>
              <Horizontal gap={1} as="span">
                <TeamAvatar size={16} team={anyTeam} />
                {anyTeam.officialName}
              </Horizontal>
            </LabelM>
          ),
        });
      }
      return acc;
    },
    [ALL_TEAMS_ITEM]
  );

  const competitions = so5Scores.reduce(
    (acc, score) => {
      const { anyGame } = score.anyPlayerGameStats;
      if (!anyGame) return acc;
      const { competition } = anyGame;
      if (!acc.find(t => t.value === competition.slug)) {
        acc.push({
          value: competition.slug,
          label: <LabelM bold>{competition.displayName}</LabelM>,
        });
      }
      return acc;
    },
    [ALL_COMPETITIONS_ITEM]
  );

  return {
    [FilterKey.TEAM]: teams,
    [FilterKey.COMPETITION]: competitions,
    [FilterKey.HOME_AWAY]: homeAwayOptions,
    [FilterKey.MODE]: [],
  };
};

useGetFiltersOptions.fragments = {
  playerGameScoreInterface: gql`
    fragment useGetFiltersOptions_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      anyPlayerGameStats {
        id
        anyTeam {
          slug
          officialName
          ...TeamAvatar_team
        }
        anyGame {
          id
          competition {
            slug
            displayName
          }
          homeTeam {
            slug
            ...TeamAvatar_team
          }
          awayTeam {
            slug
            ...TeamAvatar_team
          }
        }
      }
    }
    ${TeamAvatar.fragments.team}
  ` as TypedDocumentNode<useGetFiltersOptions_playerGameScoreInterface>,
};
